import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2, X, Loader2, Crown, ExternalLink } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  hasActiveSubscription: boolean;
  onManageSubscription: () => void;
}

export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  hasActiveSubscription,
  onManageSubscription,
}: DeleteAccountModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expectedConfirmText = t('profile:modals.deleteAccount.confirmText');

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (confirmText !== expectedConfirmText) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onConfirm();
    } catch (err) {
      console.error('Delete account error:', err);
      setError(t('profile:modals.deleteAccount.error'));
      setIsDeleting(false);
    }
  };

  return createPortal(
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="!text-lg !font-semibold text-white">{t('profile:modals.deleteAccount.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            {hasActiveSubscription && (
              <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-[15px] p-4">
                <div className="flex items-start gap-3">
                  <Crown className="w-5 h-5 text-pp-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-pp-orange">{t('profile:modals.deleteAccount.activeSubscription')}</h3>
                    <p className="text-sm text-white/70 mt-1">
                      {t('profile:modals.deleteAccount.subscriptionWarning')}
                    </p>
                    <button
                      onClick={onManageSubscription}
                      className="mt-2 text-sm font-medium text-pp-orange hover:text-pp-orange/80 flex items-center gap-1"
                    >
                      {t('profile:modals.deleteAccount.manageSubscription')}
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-red-500/20 border border-red-500/30 rounded-[15px] p-4">
              <h3 className="font-medium text-red-400 mb-2">{t('profile:modals.deleteAccount.deletedDataTitle')}</h3>
              <ul className="text-sm text-white/70 space-y-1">
                <li>&bull; {t('profile:modals.deleteAccount.dataItem1')}</li>
                <li>&bull; {t('profile:modals.deleteAccount.dataItem2')}</li>
                <li>&bull; {t('profile:modals.deleteAccount.dataItem3')}</li>
                <li>&bull; {t('profile:modals.deleteAccount.dataItem4')}</li>
                <li>&bull; {t('profile:modals.deleteAccount.dataItem5')}</li>
                {hasActiveSubscription && (
                  <li>&bull; {t('profile:modals.deleteAccount.dataItem6')}</li>
                )}
              </ul>
            </div>

            <div>
              <p
                className="text-sm text-white/70 mb-2"
                dangerouslySetInnerHTML={{
                  __html: t('profile:modals.deleteAccount.confirmPrompt', { confirmText: expectedConfirmText }),
                }}
              />
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder={expectedConfirmText}
                disabled={isDeleting}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
              />
            </div>

            {error && (
              <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-[10px]">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirmText !== expectedConfirmText || isDeleting}
              className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 disabled:bg-red-500/50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('profile:modals.deleteAccount.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  {t('profile:modals.deleteAccount.confirmButton')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
